import * as Sentry from "@sentry/react";
import { initSentry, SentryEnvironment } from "core-integration/src/sentry";

// One id per tab load, so logs from different tabs can be told apart.
// crypto.randomUUID is missing on old and insecure-context WebViews.
const tabId =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT;

// In dev mode, check for test URL param to bypass URL filtering
const isTestMode =
  import.meta.env.DEV &&
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("testError") === "true";

const allowUrls = isTestMode
  ? undefined
  : [/premierleague\.com/, /ismgames\.com/];

if (sentryEnvironment) {
  initSentry({
    dsn: "https://81d88aa7bc0b46739acf531684529abf@o118622.ingest.us.sentry.io/1462585",
    environment: sentryEnvironment as SentryEnvironment,
    allowUrls,
    ignoreErrors: [
      /Unexpected token/,
      /from accessing a cross-origin frame/,
      /NetworkError when attempting to fetch/,
      /Failed to fetch/,
      /Unexpected end of input/,
      /Service Unavailable/,
      /contextMenuMessageHandler/,
      /find variable: webkit/,
      /NPObject deleted/,
      /touchDownX/,
      /SecurityError/,
      /ntp is not defined/,
      /Permission denied to access property/,
      /NPMethod called on non-NPObject/,
      /\[Immer\] minified error nr: 18 'ES5'/,
      /NoModificationAllowedError/,
      /Trust Tokens/,
      /send-redemption-record/,
      /GVLError/,
      /token-redemption/,
      /Importing a module script failed/,
      /FirebaseError/,
      /firebaseinstallations/,
      /IDBDatabase/,
      // Adobe CCEverywhere SDK uses Object.hasOwn (ES2022), which throws on
      // old WebViews (e.g. Chrome 92). Out of our control, so ignore it.
      /Object\.hasOwn is not a function/,
      // Adobe CCEverywhere SDK uses crypto.randomUUID, unavailable on old/
      // insecure-context WebViews. Out of our control, so ignore it.
      /crypto\.randomUUID is not a function/,
      // Firebase touches indexedDB, which some iOS WKWebViews don't expose.
      /Can't find variable: indexedDB/,
      // Third-party code probes the iOS native bridge (window.webkit.messageHandlers)
      // when it isn't present. Out of our control, so ignore it.
      /webkit\.messageHandlers/,
      /translateNotifyError/,
      // Native-app JS bridge stat method missing in some app builds.
      /statWebViewClickEvent is not a function/,
      // Browser "translate page" feature mutates React-managed nodes, breaking
      // React's removeChild during reconciliation. Triggered externally.
      /Failed to execute 'removeChild' on 'Node'/,
      // Adobe CCEverywhere analytics (Alloy) network request failures.
      /\[ccEverywhereAlloy\]/,
      // TODO: Keep an eye on https://github.com/adobe/react-spectrum/pull/7742 and remove this once it's merged
      // See https://premierleague.atlassian.net/browse/FPL-1728 for more details
      /ResizeObserver loop completed with undelivered notifications/,
    ],
  });
  Sentry.setAttribute("tab_id", tabId);
}
